/**
 * PricingScreen — the Pricing admin surface. Three stacked sections:
 *   1. Coupon Ladder editor (per program).
 *   2. Adjustments — accordion groups of adjustment-table editors, driven by the
 *      ADJ_GROUPS config (ported from digital_sizer/pages/AdminPricing.tsx).
 *   3. Pricing Arrangements — master/detail (left list, right per-borrower-class
 *      rate editor) with a "New arrangement" create dialog and History drawer.
 *
 * Ported from digital_sizer/pages/AdminPricing.tsx into eastviewos: react-query
 * swapped for plain useState/useEffect via adminPricingApi, Tailwind/`card`
 * classes recreated with MUI sx + Flx/Txt to match the Grids/Rules aesthetic.
 * The screen owns its own scroll.
 */
import { useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import AddRounded from "@mui/icons-material/AddRounded";
import HistoryRounded from "@mui/icons-material/HistoryRounded";
import PaidOutlined from "@mui/icons-material/PaidOutlined";
import TuneRounded from "@mui/icons-material/TuneRounded";

import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import { usePermissionsHook } from "../../hooks/usePermissionsHook";
import ChangeHistoryDrawer from "../../components/admin/ChangeHistoryDrawer";
import { adminPricingApi } from "../../api/adminPricingApi";
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";
import CouponLadderEditor from "./CouponLadderEditor";
import AdjustmentTableEditor, { SHAPE } from "./AdjustmentTableEditor";
import TierMarginsSection from "./TierMarginsSection";
import RtlBaseRatesSection from "./RtlBaseRatesSection";
import { teal } from "../../theme/colors";

// ── Adjustment groups config (ported from AdminPricing.tsx ADJ_GROUPS) ───────
const ADJ_GROUPS = [
  {
    heading: "Pricing Adjustments (LLPA)",
    panels: [
      { key: "ficoLtvAdjustments", title: "FICO × LTV", shape: SHAPE.FICO_LTV_GRID, rowLabel: "FICO" },
      { key: "dscrAdjustments", title: "DSCR × LTV", shape: SHAPE.DSCR_LTV_GRID, rowLabel: "DSCR Band" },
      { key: "cashOutAdjustments", title: "Cash-out LTV", shape: SHAPE.LTV_BAND_MAP },
    ],
  },
  {
    heading: "Property / Eligibility Adjustments",
    panels: [
      { key: "ioLTV", title: "Interest-Only LTV", shape: SHAPE.LTV_BAND_MAP },
      { key: "condoLTV", title: "Condo LTV", shape: SHAPE.LTV_BAND_MAP },
      { key: "nonWarrantableCondoLTV", title: "Non-Warrantable Condo LTV", shape: SHAPE.LTV_BAND_MAP },
      { key: "twoToFourUnitLTV", title: "2–4 Unit LTV", shape: SHAPE.LTV_BAND_MAP },
      { key: "fiveToNineUnitLTV", title: "5–9 Unit LTV", shape: SHAPE.LTV_BAND_MAP },
      { key: "crossCollLTV", title: "Cross-Collateral LTV", shape: SHAPE.LTV_BAND_MAP },
    ],
  },
  {
    heading: "Prepay & Loan Sizing",
    panels: [
      { key: "pppAdjustments", title: "PPP × LTV", shape: SHAPE.PPP_LTV_GRID, rowLabel: "PPP" },
      { key: "loanAmountAdjustments", title: "Loan Amount Buckets", shape: SHAPE.LOAN_AMOUNT_BUCKETS },
    ],
  },
];

const RATE_FIELDS = [
  { key: "min_rate", label: "Min Rate" },
  { key: "max_leverage_adjustment", label: "Max Lev Adj" },
  { key: "guideline_adjustments", label: "Guideline Adj" },
  { key: "cash_out_adjustment", label: "Cash-Out Adj" },
];

const SLUG_RE = /^[a-z0-9]+(?:[-_][a-z0-9]+)*$/;
const arrangementKey = (a) => `${a.program}::${a.arrangement}`;

const sectionCardSx = { border: "1px solid #e5e7eb", borderRadius: 3.5, bgcolor: "#fff", overflow: "hidden" };
const sectionTitleSx = { fontSize: 14, fontWeight: 600, color: "#0f172a" };

// ── Adjustments section ──────────────────────────────────────────────────────
function AdjustmentsSection() {
  // Track which accordion is open so the editor only mounts (and fetches) when
  // expanded — the source mounted them lazily via Suspense for the same reason.
  const [openKey, setOpenKey] = useState(null);
  return (
    <Box sx={sectionCardSx}>
      <Flx ac g={1.5} sx={{ px: 2.5, py: 1.75, borderBottom: "1px solid #eef2f5" }}>
        <TuneRounded sx={{ fontSize: 18, color: teal[700] }} />
        <Txt sx={sectionTitleSx}>Adjustments</Txt>
      </Flx>
      <Box sx={{ p: 2.5 }}>
        {ADJ_GROUPS.map((group) => (
          <Box key={group.heading} sx={{ mb: 2.5, "&:last-of-type": { mb: 0 } }}>
            <Txt
              sx={{
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "#94a3b8",
                mb: 1,
              }}
            >
              {group.heading}
            </Txt>
            {group.panels.map((p) => (
              <Accordion
                key={p.key}
                disableGutters
                expanded={openKey === p.key}
                onChange={(_, isExp) => setOpenKey(isExp ? p.key : null)}
                elevation={0}
                sx={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "10px !important",
                  mb: 0.75,
                  "&:before": { display: "none" },
                  overflow: "hidden",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreRounded sx={{ fontSize: 20, color: "#64748b" }} />}
                  sx={{
                    minHeight: 44,
                    px: 1.75,
                    "& .MuiAccordionSummary-content": { my: 0.75 },
                    "&:hover": { bgcolor: "#f8fafc" },
                    "&.Mui-expanded": { bgcolor: "#f1f6f9", borderBottom: "1px solid #e5e7eb" },
                  }}
                >
                  <Txt sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{p.title}</Txt>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 2, bgcolor: "#fcfcfd" }}>
                  {openKey === p.key && <AdjustmentTableEditor tableKey={p.key} shape={p.shape} rowLabel={p.rowLabel} />}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ── Create-arrangement dialog ────────────────────────────────────────────────
function CreateDialog({ open, onClose, onCreate, busy }) {
  const [form, setForm] = useState({ program: "RTL", arrangement_id: "", display_name: "" });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({ program: "RTL", arrangement_id: "", display_name: "" });
      setError(null);
    }
  }, [open]);

  const submit = () => {
    setError(null);
    const id = form.arrangement_id.trim().toLowerCase();
    if (!SLUG_RE.test(id)) {
      setError("ID must be lowercase kebab-case (a-z, 0-9, -)");
      return;
    }
    if (!form.display_name.trim()) {
      setError("Display name is required");
      return;
    }
    onCreate({ ...form, arrangement_id: id }, setError);
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>New pricing arrangement</DialogTitle>
      <DialogContent>
        <Flx column g={2} sx={{ mt: 1 }}>
          <TextField
            select
            label="Program"
            size="small"
            value={form.program}
            onChange={(e) => setForm((f) => ({ ...f, program: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="RTL">RTL</MenuItem>
            <MenuItem value="GUC">GUC</MenuItem>
          </TextField>
          <TextField
            label="Arrangement ID (slug)"
            size="small"
            placeholder="e.g. capital-partners-1"
            value={form.arrangement_id}
            onChange={(e) => setForm((f) => ({ ...f, arrangement_id: e.target.value.toLowerCase() }))}
            helperText="Lowercase kebab-case (a-z, 0-9, -)"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Display name"
            size="small"
            value={form.display_name}
            onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
          {error && <Txt sx={{ fontSize: 12.5, color: "#dc2626" }}>{error}</Txt>}
        </Flx>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={busy} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button variant="contained" disableElevation onClick={submit} disabled={busy} sx={{ textTransform: "none" }}>
          {busy ? "Creating…" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Arrangements master/detail section ───────────────────────────────────────
function ArrangementsSection() {
  const [arrangements, setArrangements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState(null);
  const [editing, setEditing] = useState(null);
  const [commitMsg, setCommitMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const load = () => {
    setLoading(true);
    return adminPricingApi
      .getPricingArrangements()
      .then((data) => setArrangements(Array.isArray(data) ? data : []))
      .catch(() => setArrangements([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  // Auto-select the first arrangement once data lands.
  useEffect(() => {
    if (arrangements.length === 0 || selectedKey != null) return;
    const first = arrangements[0];
    setSelectedKey(arrangementKey(first));
    setEditing(JSON.parse(JSON.stringify(first)));
  }, [arrangements, selectedKey]);

  // Re-hydrate the editing draft from the underlying record after a reload.
  useEffect(() => {
    if (selectedKey == null) return;
    const match = arrangements.find((a) => arrangementKey(a) === selectedKey);
    if (match) setEditing(JSON.parse(JSON.stringify(match)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrangements]);

  const selectItem = (a) => {
    setSelectedKey(arrangementKey(a));
    setEditing(JSON.parse(JSON.stringify(a)));
  };

  const onSave = () => {
    if (!editing) return;
    setSaving(true);
    adminPricingApi
      .savePricingArrangement(editing, commitMsg)
      .then(() => {
        setCommitMsg("");
        return load();
      })
      .catch((e) => window.alert(`Save failed: ${e?.response?.data?.detail || e?.message || e}`))
      .finally(() => setSaving(false));
  };

  const onCreate = (body, setError) => {
    setCreating(true);
    adminPricingApi
      .createPricingArrangement(body)
      .then((created) => {
        setCreateOpen(false);
        return load().then(() => {
          setSelectedKey(arrangementKey(created));
          setEditing(JSON.parse(JSON.stringify(created)));
        });
      })
      .catch((e) => setError(e?.response?.data?.detail || e?.message || "Failed to create arrangement"))
      .finally(() => setCreating(false));
  };

  const setRate = (cls, key, raw) => {
    const v = Number(raw);
    setEditing((prev) =>
      prev
        ? { ...prev, rates: { ...prev.rates, [cls]: { ...prev.rates[cls], [key]: Number.isFinite(v) ? v : 0 } } }
        : prev,
    );
  };

  const rateClasses = useMemo(() => (editing?.rates ? Object.keys(editing.rates) : []), [editing]);

  return (
    <Box sx={sectionCardSx}>
      <Flx ac jb g={1} sx={{ px: 2.5, py: 1.75, borderBottom: "1px solid #eef2f5" }}>
        <Flx ac g={1.5}>
          <PaidOutlined sx={{ fontSize: 18, color: teal[700] }} />
          <Txt sx={sectionTitleSx}>Pricing Arrangements</Txt>
        </Flx>
        <Button size="small" variant="outlined" startIcon={<AddRounded sx={{ fontSize: 16 }} />} onClick={() => setCreateOpen(true)} sx={{ textTransform: "none" }}>
          New arrangement
        </Button>
      </Flx>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "300px minmax(0, 1fr)" },
          minHeight: 280,
        }}
      >
        {/* Master list */}
        <Box sx={{ borderRight: { md: "1px solid #eef2f5" }, bgcolor: "#fcfcfd", p: 1.25 }}>
          {loading ? (
            <Flx center sx={{ py: 5 }}>
              <CircularProgress size={20} sx={{ color: teal[700] }} />
            </Flx>
          ) : arrangements.length === 0 ? (
            <Flx column center sx={{ py: 5, px: 2, textAlign: "center", color: "#94a3b8" }}>
              <PaidOutlined sx={{ fontSize: 30, mb: 1 }} />
              <Txt sx={{ fontSize: 12.5 }}>No pricing arrangements yet. Create your first to get started.</Txt>
            </Flx>
          ) : (
            arrangements.map((a) => {
              const k = arrangementKey(a);
              const sel = k === selectedKey;
              return (
                <Box
                  key={k}
                  component="button"
                  type="button"
                  onClick={() => selectItem(a)}
                  sx={{
                    appearance: "none",
                    textAlign: "left",
                    cursor: "pointer",
                    width: "100%",
                    border: "1px solid",
                    borderColor: sel ? teal[700] : "transparent",
                    bgcolor: sel ? "#f1f6f9" : "transparent",
                    borderRadius: 1.5,
                    px: 1.25,
                    py: 0.85,
                    mb: 0.25,
                    "&:hover": { bgcolor: sel ? "#e4eef3" : "#e8eef2" },
                  }}
                >
                  <Flx ac jb g={1}>
                    <Txt sx={{ fontSize: 13, fontWeight: sel ? 700 : 600, color: "#0f172a" }}>
                      {a.display_name || a.arrangement}
                    </Txt>
                    <Txt sx={{ fontSize: 10.5, color: "#94a3b8" }}>v{a.version || 1}</Txt>
                  </Flx>
                  <Txt sx={{ fontSize: 11.5, color: "#64748b", mt: 0.25 }}>
                    {a.program} · {a.arrangement}
                  </Txt>
                </Box>
              );
            })
          )}
        </Box>

        {/* Detail editor */}
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {!editing ? (
            <Flx column center sx={{ height: "100%", minHeight: 200, color: "#94a3b8", textAlign: "center", px: 3 }}>
              <PaidOutlined sx={{ fontSize: 36 }} />
              <Txt sx={{ fontSize: 15, fontWeight: 600, color: "#334155", mt: 1.5 }}>Select an arrangement</Txt>
              <Txt sx={{ fontSize: 13, mt: 0.5, maxWidth: 340 }}>Choose an arrangement on the left to edit its rates.</Txt>
            </Flx>
          ) : (
            <Box>
              <Flx ac jb g={1} sx={{ mb: 0.5 }}>
                <Txt sx={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>
                  {editing.display_name || editing.arrangement}
                </Txt>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<HistoryRounded sx={{ fontSize: 16 }} />}
                  onClick={() => setHistoryOpen(true)}
                  sx={{ textTransform: "none" }}
                >
                  History
                </Button>
              </Flx>
              <Txt sx={{ fontSize: 13, color: "#64748b", mb: 2 }}>
                {editing.program} · {editing.arrangement}
              </Txt>

              <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 2, overflow: "auto", mb: 1.5 }}>
                <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                  <Box
                    component="thead"
                    sx={{
                      "& th": {
                        bgcolor: "#f1f6f9",
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#475569",
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        py: 1,
                        px: 1.5,
                        borderBottom: "1px solid #e5e7eb",
                        textAlign: "right",
                        whiteSpace: "nowrap",
                      },
                      "& th:first-of-type": { textAlign: "left" },
                    }}
                  >
                    <Box component="tr">
                      <Box component="th">Class</Box>
                      {RATE_FIELDS.map((f) => (
                        <Box component="th" key={f.key}>
                          {f.label}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Box
                    component="tbody"
                    sx={{
                      "& td": { py: 0.5, px: 1.5, borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" },
                      "& tr:last-of-type td": { borderBottom: "none" },
                      "& tr:nth-of-type(even)": { bgcolor: "#fcfcfd" },
                      "& tr:hover": { bgcolor: "#f8fafc" },
                    }}
                  >
                    {rateClasses.map((cls) => (
                      <Box component="tr" key={cls}>
                        <Box component="td" sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>
                          {cls}
                        </Box>
                        {RATE_FIELDS.map((f) => (
                          <Box component="td" key={f.key} sx={{ textAlign: "right" }}>
                            <TextField
                              type="number"
                              size="small"
                              value={editing.rates[cls]?.[f.key] ?? 0}
                              onChange={(e) => setRate(cls, f.key, e.target.value)}
                              inputProps={{ step: 0.00025, style: { textAlign: "right" } }}
                              sx={{ width: 110, "& input": { py: 0.5, fontSize: 13, fontVariantNumeric: "tabular-nums" } }}
                            />
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>

              <Txt sx={{ fontSize: 11.5, color: "#94a3b8", mb: 2 }}>
                Last modified by {editing.updated_by || "—"}
                {editing.updated_at ? ` · ${formatDateTime(editing.updated_at, "datetime")}` : ""} · v{editing.version || 1}
              </Txt>

              <TextField
                size="small"
                fullWidth
                label="Commit message (optional)"
                placeholder="Why are you saving this change?"
                value={commitMsg}
                onChange={(e) => setCommitMsg(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 1.5 }}
              />

              <Button variant="contained" disableElevation onClick={onSave} disabled={saving} sx={{ textTransform: "none", fontWeight: 600, px: 2.5 }}>
                {saving ? "Saving…" : "Save"}
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      <CreateDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreate={onCreate} busy={creating} />
      {editing && (
        <ChangeHistoryDrawer
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          entityType="pricing_arrangement"
          entityId={`${editing.program}/${editing.arrangement}`}
        />
      )}
    </Box>
  );
}

// Pill tab switcher — house style (#eef3f5 track, #e3e9ec border, white selected).
function PillTabs({ value, onChange, tabs }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        gap: 0.5,
        p: "3px",
        borderRadius: "10px",
        background: "#eef3f5",
        border: "1px solid #e3e9ec",
      }}
    >
      {tabs.map((t) => {
        const sel = t.value === value;
        return (
          <Box
            key={t.value}
            component="button"
            type="button"
            onClick={() => onChange(t.value)}
            sx={{
              appearance: "none",
              cursor: "pointer",
              border: "none",
              borderRadius: "8px",
              px: 1.75,
              py: 0.6,
              fontSize: 13,
              fontFamily: "inherit",
              fontWeight: sel ? 700 : 500,
              color: sel ? "#0f172a" : "#64748b",
              background: sel ? "#fff" : "transparent",
              boxShadow: sel ? "0 1px 2px rgba(15,23,42,0.08)" : "none",
              "&:hover": { color: "#0f172a" },
            }}
          >
            {t.label}
          </Box>
        );
      })}
    </Box>
  );
}

export default function PricingScreen() {
  const { isAdmin } = usePermissionsHook();
  const [tab, setTab] = useState("pricing");

  // Defend against a non-admin landing on the admin tab (e.g. permission change).
  useEffect(() => {
    if (tab === "admin" && !isAdmin) setTab("pricing");
  }, [tab, isAdmin]);

  const onAdmin = tab === "admin" && isAdmin;

  return (
    <Box sx={{ height: "100%", overflowY: "auto", bgcolor: "#f8fafc" }}>
      <Box sx={{ maxWidth: 1280, mx: "auto", p: { xs: 2, md: 3 } }}>
        <Flx ac jb g={1.5} sx={{ mb: 2.5 }}>
          <Txt sx={{ fontSize: 22, fontWeight: 800, color: "#0f172a" }}>
            Pricing
          </Txt>
          {/* Admin Config tab is admin-only (the tier-margin / base-rate
              endpoints are admin-gated server-side too). */}
          {isAdmin && (
            <PillTabs
              value={tab}
              onChange={setTab}
              tabs={[
                { value: "pricing", label: "Pricing" },
                { value: "admin", label: "Admin Config" },
              ]}
            />
          )}
        </Flx>
        {onAdmin ? (
          <Flx column g={3}>
            <TierMarginsSection />
            <RtlBaseRatesSection />
          </Flx>
        ) : (
          <Flx column g={3}>
            <CouponLadderEditor />
            <AdjustmentsSection />
            <ArrangementsSection />
          </Flx>
        )}
      </Box>
    </Box>
  );
}
